import { z } from 'zod';
export const BaseEventPayloadSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    timestamp: z.string().datetime(),
});
export const OrchestratorEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('orchestrator:start_pipeline'),
        payload: BaseEventPayloadSchema.extend({
            pipelineId: z.string().uuid(),
            totalSteps: z.number(),
            globalContext: z.record(z.any()).optional(),
        }),
    }),
    z.object({
        type: z.literal('orchestrator:dispatch_agent'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            prompt: z.string(),
            model: z.string().optional(),
            options: z.record(z.any()).optional(),
        }),
    }),
    z.object({
        type: z.literal('orchestrator:stop_pipeline'),
        payload: BaseEventPayloadSchema.extend({
            pipelineId: z.string().uuid(),
            success: z.boolean(),
            totalSteps: z.number(),
            completedSteps: z.number(),
            totalCost: z.number(),
            totalExecutionTime: z.number(),
            errors: z.array(z.string()).optional(),
        }),
    }),
]);
export const AgentEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('agent:start'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            runnerType: z.string(),
            model: z.string().optional(),
            prompt: z.string().optional(),
        }),
    }),
    z.object({
        type: z.literal('agent:progress'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            message: z.string(),
            percentage: z.number().min(0).max(100).optional(),
        }),
    }),
    z.object({
        type: z.literal('agent:complete'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            success: z.boolean(),
            output: z.string().optional(),
            metadata: z.object({
                executionTime: z.number(),
                tokensUsed: z.number().optional(),
                cost: z.number().optional(),
                model: z.string().optional(),
            }).optional(),
        }),
    }),
    z.object({
        type: z.literal('agent:error'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            error: z.string(),
            metadata: z.object({
                executionTime: z.number().optional(),
            }).optional(),
        }),
    }),
]);
export const ClaudeEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('claude:start'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            model: z.string(),
            command: z.string(),
            inputFormat: z.string(),
            outputFormat: z.string(),
        }),
    }),
    z.object({
        type: z.literal('claude:progress'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            streamEvent: z.string(),
            message: z.string().optional(),
            tokensStreamed: z.number().optional(),
        }),
    }),
    z.object({
        type: z.literal('claude:complete'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            success: z.boolean(),
            claudeMetadata: z.object({
                sessionId: z.string(),
                durationMs: z.number(),
                durationApiMs: z.number(),
                totalCostUsd: z.number(),
                usage: z.object({
                    inputTokens: z.number(),
                    outputTokens: z.number(),
                    cacheReadTokens: z.number().optional(),
                }),
            }),
        }),
    }),
    z.object({
        type: z.literal('claude:error'),
        payload: BaseEventPayloadSchema.extend({
            agentName: z.string(),
            error: z.string(),
            claudeError: z.object({
                type: z.string(),
                details: z.string().optional(),
            }).optional(),
        }),
    }),
]);
export const ToolEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('tool:start'),
        payload: BaseEventPayloadSchema.extend({
            toolName: z.string(),
            parameters: z.record(z.any()).optional(),
            context: z.string().optional(),
        }),
    }),
    z.object({
        type: z.literal('tool:progress'),
        payload: BaseEventPayloadSchema.extend({
            toolName: z.string(),
            message: z.string(),
            percentage: z.number().min(0).max(100).optional(),
            stepInfo: z.string().optional(),
        }),
    }),
    z.object({
        type: z.literal('tool:success'),
        payload: BaseEventPayloadSchema.extend({
            toolName: z.string(),
            result: z.any(),
            executionTime: z.number(),
        }),
    }),
    z.object({
        type: z.literal('tool:error'),
        payload: BaseEventPayloadSchema.extend({
            toolName: z.string(),
            error: z.string(),
            executionTime: z.number().optional(),
        }),
    }),
]);
export const SessionEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('session:start'),
        payload: BaseEventPayloadSchema.extend({
            sessionType: z.string(),
            configuration: z.record(z.any()).optional(),
        }),
    }),
    z.object({
        type: z.literal('session:stop'),
        payload: BaseEventPayloadSchema.extend({
            sessionType: z.string(),
            duration: z.number(),
            totalOperations: z.number(),
        }),
    }),
]);
export const DatabaseEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('prompt:received'),
        payload: z.object({
            promptId: z.string().uuid(),
            sessionId: z.string().uuid(),
            deviceId: z.string().uuid(),
            promptText: z.string(),
            metadata: z.record(z.any()).optional(),
            createdAt: z.string().datetime(),
        }),
    }),
    z.object({
        type: z.literal('prompt:updated'),
        payload: z.object({
            promptId: z.string().uuid(),
            responseText: z.string().optional(),
            model: z.string().optional(),
            agentName: z.string().optional(),
            inputTokens: z.number().optional(),
            outputTokens: z.number().optional(),
            costUsd: z.number().optional(),
            metadata: z.record(z.any()).optional(),
        }),
    }),
    z.object({
        type: z.literal('execution:created'),
        payload: z.object({
            executionId: z.string().uuid(),
            promptId: z.string().uuid().optional(),
            sessionId: z.string().uuid(),
            agent: z.string().optional(),
            type: z.string().optional(),
            status: z.string(),
            createdAt: z.string().datetime(),
        }),
    }),
    z.object({
        type: z.literal('execution:updated'),
        payload: z.object({
            executionId: z.string().uuid(),
            promptId: z.string().uuid().optional(),
            status: z.string(),
            result: z.string().optional(),
            errorMessage: z.string().optional(),
            completedAt: z.string().datetime().optional(),
            metadata: z.record(z.any()).optional(),
        }),
    }),
]);
export const QwickForgeEventSchema = z.union([
    OrchestratorEventSchema,
    AgentEventSchema,
    ClaudeEventSchema,
    ToolEventSchema,
    SessionEventSchema,
    DatabaseEventSchema,
]);
export const EVENT_TOPICS = {
    PIPELINE_START: 'pipeline:start',
    PIPELINE_DISPATCH_AGENT: 'pipeline:dispatch_agent',
    PIPELINE_ERROR: 'pipeline:error',
    PIPELINE_COMPLETE: 'pipeline:complete',
    AGENT_START: 'agent:start',
    AGENT_PROGRESS: 'agent:progress',
    AGENT_COMPLETE: 'agent:complete',
    AGENT_ERROR: 'agent:error',
    CLAUDE_START: 'claude:start',
    CLAUDE_PROGRESS: 'claude:progress',
    CLAUDE_COMPLETE: 'claude:complete',
    CLAUDE_ERROR: 'claude:error',
    TOOL_START: 'tool:start',
    TOOL_PROGRESS: 'tool:progress',
    TOOL_SUCCESS: 'tool:success',
    TOOL_ERROR: 'tool:error',
    SESSION_START: 'session:start',
    SESSION_STOP: 'session:stop',
    PROMPT_RECEIVED: 'prompt:received',
    PROMPT_UPDATED: 'prompt:updated',
    PROMPT_FAILED: 'prompt:failed',
    EXECUTION_CREATED: 'execution:created',
    EXECUTION_UPDATED: 'execution:updated',
    EXECUTION_FAILED: 'execution:failed',
    PIPELINE_ANALYSIS_WARNING: 'pipeline:analysis:warning',
    PIPELINE_ANALYSIS_STARTED: 'pipeline:analysis:started',
    PIPELINE_ANALYSIS_COMPLETED: 'pipeline:analysis:completed',
    PIPELINE_ANALYSIS_FAILED: 'pipeline:analysis:failed',
    PIPELINE_EXECUTION_STARTED: 'pipeline:execution:started',
    PIPELINE_EXECUTION_COMPLETED: 'pipeline:execution:completed',
    PIPELINE_COST_ESTIMATED: 'pipeline:cost:estimated',
    PIPELINE_COST_THRESHOLD_EXCEEDED: 'pipeline:cost:threshold:exceeded',
    PIPELINE_PARSING_FAILED: 'pipeline:parsing:failed',
    PIPELINE_SIMPLE_ANALYSIS_EMPTY: 'pipeline:simple:analysis:empty',
};
export const AgentStartEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    promptId: z.string().uuid().optional(),
    agentName: z.string(),
    startedAt: z.date().or(z.string().datetime()),
});
export const AgentCompleteEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    promptId: z.string().uuid(),
    agentName: z.string(),
    success: z.boolean(),
    output: z.string().optional(),
    metadata: z.record(z.any()).optional(),
});
export const AgentErrorEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    error: z.string(),
    metadata: z.record(z.any()).optional(),
    retrying: z.boolean().optional(),
    permanent: z.boolean().optional(),
    attempt: z.number().optional(),
    maxAttempts: z.number().optional(),
});
export const AgentProgressEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    message: z.string(),
    percentage: z.number().min(0).max(100).optional(),
});
export const ClaudeStartEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    model: z.string(),
    command: z.string(),
    inputFormat: z.string(),
    outputFormat: z.string(),
});
export const ClaudeProgressEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    streamEvent: z.string(),
    message: z.string().optional(),
    tokensStreamed: z.number().optional(),
});
export const ClaudeUsageSchema = z.object({
    input_tokens: z.number().optional(),
    output_tokens: z.number().optional(),
    cache_creation_input_tokens: z.number().optional(),
    cache_read_input_tokens: z.number().optional(),
});
export const ClaudeMetadataSchema = z.object({
    model: z.string().optional(),
    model_used: z.string().optional(),
    sessionId: z.string().optional(),
    durationMs: z.number().optional(),
    durationApiMs: z.number().optional(),
    totalCostUsd: z.number().optional(),
    usage: ClaudeUsageSchema.optional(),
});
export const ClaudeCompleteEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    promptId: z.string().uuid().optional(),
    agentName: z.string(),
    success: z.boolean(),
    result: z.object({
        output: z.string().optional(),
        text: z.string().optional(),
    }).or(z.string()).optional(),
    claudeMetadata: ClaudeMetadataSchema.optional(),
});
export const ClaudeErrorEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    error: z.string(),
    claudeError: z.record(z.any()).optional(),
});
export const OrchestratorStartPipelineEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    pipelineId: z.string().uuid(),
    totalSteps: z.number(),
    globalContext: z.record(z.any()).optional(),
});
export const OrchestratorDispatchAgentEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    agentName: z.string(),
    prompt: z.string(),
    model: z.string().optional(),
    options: z.record(z.any()).optional(),
});
export const OrchestratorStopPipelineEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    pipelineId: z.string().uuid(),
    success: z.boolean(),
    totalSteps: z.number(),
    completedSteps: z.number(),
    totalCost: z.number(),
    totalExecutionTime: z.number(),
    errors: z.array(z.string()).optional(),
});
export const ToolStartEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    toolName: z.string(),
    parameters: z.record(z.any()).optional(),
    context: z.string().optional(),
});
export const ToolProgressEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    toolName: z.string(),
    message: z.string(),
    percentage: z.number().min(0).max(100).optional(),
    stepInfo: z.string().optional(),
});
export const ToolSuccessEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    toolName: z.string(),
    result: z.any(),
    executionTime: z.number(),
});
export const ToolErrorEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid().optional(),
    toolName: z.string(),
    error: z.string(),
    executionTime: z.number().optional(),
});
export const SessionStartEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid(),
    sessionType: z.string(),
    configuration: z.record(z.any()).optional(),
});
export const SessionStopEventSchema = z.object({
    executionId: z.string().uuid(),
    sessionId: z.string().uuid(),
    sessionType: z.string(),
    duration: z.number(),
    totalOperations: z.number(),
});
export const PromptReceivedEventSchema = z.object({
    promptId: z.string().uuid(),
    sessionId: z.string().uuid(),
    deviceId: z.string().uuid(),
    promptText: z.string(),
    agentName: z.string(),
    metadata: z.record(z.any()).optional(),
    createdAt: z.string().datetime(),
});
export const PromptUpdatedEventSchema = z.object({
    promptId: z.string().uuid(),
    responseText: z.string().optional(),
    model: z.string().optional(),
    agentName: z.string().optional(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    costUsd: z.number().optional(),
    metadata: z.record(z.any()).optional(),
});
export const ExecutionCreatedEventSchema = z.object({
    executionId: z.string().uuid(),
    promptId: z.string().uuid().optional(),
    sessionId: z.string().uuid(),
    agent: z.string().optional(),
    type: z.string().optional(),
    status: z.string(),
    createdAt: z.string().datetime(),
});
export const ExecutionUpdatedEventSchema = z.object({
    executionId: z.string().uuid(),
    promptId: z.string().uuid().optional(),
    status: z.string(),
    result: z.string().optional(),
    errorMessage: z.string().optional(),
    completedAt: z.string().datetime().optional(),
    metadata: z.record(z.any()).optional(),
});
export const ExecutionFailedEventSchema = z.object({
    promptId: z.string().uuid(),
    sessionId: z.string().uuid(),
    agentName: z.string(),
    error: z.string(),
    timestamp: z.string().datetime(),
    reason: z.string(),
});
export const PromptFailedEventSchema = z.object({
    executionId: z.string().uuid(),
    promptId: z.string().uuid(),
    error: z.string(),
    timestamp: z.string().datetime(),
    reason: z.string(),
});
export const PipelineAnalysisWarningEventSchema = z.object({
    message: z.string(),
});
export const PipelineAnalysisStartedEventSchema = z.object({
    prompt: z.string(),
});
export const PipelineAnalysisCompletedEventSchema = z.object({
    analysis: z.record(z.any()),
});
export const PipelineAnalysisFailedEventSchema = z.object({
    error: z.any(),
    fallback: z.boolean(),
});
export const PipelineExecutionStartedEventSchema = z.object({
    analysis: z.record(z.any()),
    steps: z.number(),
});
export const PipelineExecutionCompletedEventSchema = z.object({
    message: z.string().optional(),
    result: z.record(z.any()).optional(),
});
export const PipelineCostEstimatedEventSchema = z.object({
    cost: z.number(),
    threshold: z.number(),
});
export const PipelineCostThresholdExceededEventSchema = z.object({
    cost: z.number(),
    threshold: z.number(),
});
export const PipelineParsingFailedEventSchema = z.object({
    error: z.string(),
    result: z.string(),
});
export const PipelineSimpleAnalysisEmptyEventSchema = z.object({
    prompt: z.string(),
});
export const ThoughtEventSchema = z.object({
    type: z.literal('thought:created'),
    payload: z.object({
        thoughtId: z.string().uuid(),
        executionId: z.string().uuid(),
        sequenceNumber: z.number().int().positive(),
        content: z.string(),
        timestamp: z.string().datetime(),
    }),
});
export function isThoughtEvent(event) {
    return ThoughtEventSchema.safeParse(event).success;
}
export function createBaseEventPayload(executionId, sessionId) {
    return {
        executionId,
        sessionId,
        timestamp: new Date().toISOString(),
    };
}
export function getEventTopic(event) {
    return event.type;
}
export function isOrchestratorEvent(event) {
    return event.type.startsWith('orchestrator:');
}
export function isAgentEvent(event) {
    return event.type.startsWith('agent:');
}
export function isClaudeEvent(event) {
    return event.type.startsWith('claude:');
}
export function isToolEvent(event) {
    return event.type.startsWith('tool:');
}
export function isSessionEvent(event) {
    return event.type.startsWith('session:');
}
export function isDatabaseEvent(event) {
    return event.type.startsWith('prompt:') || event.type.startsWith('execution:');
}
//# sourceMappingURL=event-system.js.map